import { z } from 'zod';

/**
 * Codesleutel: de Klinger-nummering uit "Omnummeren sleutel NIEUW.xlsx" als boom van prefixen.
 * Elke knoop legt een stuk van een productcode uit (bv. 8006 = automatiseringssets, 80061 = tbv
 * kogelkranen). `explain` ontcijfert een code tot zulke segmenten; `next` geeft bij een prefix de
 * mogelijke volgende posities met label én aantal bestaande Unit4-codes.
 */
export const CodeKeyKindSchema = z.enum([
  'group', // hoofdgroep (eerste 2 of 4 cijfers, komt overeen met een werkblad)
  'class', // classificatie-knoop met label uit de sleutel
  'free', // slot dat in de sleutel als "vrij" staat
  'gap', // positie zonder eigen knoop in de sleutel (vast vulcijfer, bv. de 0 na 80)
  'unknown', // komt in Unit4 voor maar staat niet in de sleutel
]);
export type CodeKeyKind = z.infer<typeof CodeKeyKindSchema>;

export const CodeKeySegmentSchema = z.object({
  /** Code tot en met dit segment */
  prefix: z.string(),
  /** De cijfers van dit segment (1 of 2, hoofdgroep 2 of 4) */
  digits: z.string(),
  label: z.string().nullable(),
  kind: CodeKeyKindSchema,
  /** Afgeleid van een gelijkwaardige positie elders in het werkblad (facet-sleutel), niet letterlijk gevonden */
  inferred: z.boolean(),
  /** Bij `inferred`: de prefix waar het label vandaan komt */
  via: z.string().nullable(),
});
export type CodeKeySegment = z.infer<typeof CodeKeySegmentSchema>;

export const CodeExplanationSchema = z.object({
  code: z.string(),
  /** Alleen numerieke codes worden ontcijferd */
  numeric: z.boolean(),
  /** Hoofdgroep uit de sleutel (null = niet in het werkboek) */
  group: z.object({ digits: z.string(), label: z.string(), codeLength: z.number().int() }).nullable(),
  /** false zodra de code langer is dan de sleutel voor deze groep kent (bv. 14-cijferige maatproducten) */
  applies: z.boolean(),
  segments: z.array(CodeKeySegmentSchema),
  /** Cijfers die niet door de sleutel verklaard worden (maat, volgnummer of onbekende tak) */
  remainder: z.string(),
  /** Korte toelichting voor de gebruiker (bv. waarom de sleutel niet geldt) */
  note: z.string().nullable(),
});
export type CodeExplanation = z.infer<typeof CodeExplanationSchema>;

export const CodeKeyChildSchema = z.object({
  prefix: z.string(),
  digits: z.string(),
  label: z.string().nullable(),
  kind: CodeKeyKindSchema,
  inferred: z.boolean(),
  via: z.string().nullable(),
});
export type CodeKeyChild = z.infer<typeof CodeKeyChildSchema>;

/** Volgende positie in de samensteller: sleutel-kind + aantal bestaande codes in de catalogus. */
export const CatalogSeriesNextSchema = CodeKeyChildSchema.extend({
  count: z.number().int(),
});
export type CatalogSeriesNext = z.infer<typeof CatalogSeriesNextSchema>;

// ---------- Beheer van de productcode-opbouw in de app (Beheer › Productcodes) ----------

export const CodeKeySourceSchema = z.enum(['excel', 'manual']);
export type CodeKeySource = z.infer<typeof CodeKeySourceSchema>;
export const CodeKeyNodeKindSchema = z.enum(['class', 'free']);
export type CodeKeyNodeKind = z.infer<typeof CodeKeyNodeKindSchema>;

/** Hoofdgroep (tabel code_key_groups): de eerste 2 of 4 cijfers, met de regels voor die groep. */
export const CodeKeyGroupRecordSchema = z.object({
  digits: z.string(),
  label: z.string(),
  /** true = elke positie heeft een vaste betekenis ongeacht de tak (afsluiters; labels mogen geleend worden) */
  facet: z.boolean(),
  code_length: z.number().int(),
  /** Herkomst in de oorspronkelijke Excel (leeg bij een in de app aangemaakte groep) */
  sheets: z.array(z.string()),
  notes: z.string().nullable(),
  node_count: z.number().int(),
  updated_at: z.string(),
});
export type CodeKeyGroupRecord = z.infer<typeof CodeKeyGroupRecordSchema>;

/** Eén positie (tabel code_key_nodes): de code tot en met deze positie, met label. */
export const CodeKeyNodeRecordSchema = z.object({
  id: z.number().int(),
  group_digits: z.string(),
  prefix: z.string(),
  width: z.number().int(),
  label: z.string(),
  kind: CodeKeyNodeKindSchema,
  source: CodeKeySourceSchema,
  notes: z.string().nullable(),
  updated_by_name: z.string().nullable(),
  updated_at: z.string(),
});
export type CodeKeyNodeRecord = z.infer<typeof CodeKeyNodeRecordSchema>;

const digitsField = (min: number, max: number) =>
  z
    .string()
    .trim()
    .regex(/^[0-9]+$/, 'Alleen cijfers')
    .min(min)
    .max(max);
const labelField = z.string().trim().min(1, 'Label is verplicht').max(200);
const notesField = z.string().trim().max(2000).nullable().optional();

export const CodeKeyGroupBodySchema = z.object({
  digits: digitsField(2, 4),
  label: labelField,
  facet: z.boolean().default(false),
  code_length: z.number().int().min(4).max(20).default(13),
  notes: notesField,
});
export type CodeKeyGroupBody = z.infer<typeof CodeKeyGroupBodySchema>;
export const CodeKeyGroupUpdateBodySchema = CodeKeyGroupBodySchema.omit({ digits: true });
export type CodeKeyGroupUpdateBody = z.infer<typeof CodeKeyGroupUpdateBodySchema>;

export const CodeKeyNodeBodySchema = z.object({
  /** Code tot en met de nieuwe positie, bv. 800690 */
  prefix: digitsField(3, 20),
  /** Aantal cijfers van de nieuwe positie (1 of 2) */
  width: z.number().int().min(1).max(4),
  label: labelField,
  kind: CodeKeyNodeKindSchema.default('class'),
  notes: notesField,
});
export type CodeKeyNodeBody = z.infer<typeof CodeKeyNodeBodySchema>;
export const CodeKeyNodeUpdateBodySchema = z.object({
  label: labelField,
  kind: CodeKeyNodeKindSchema.default('class'),
  notes: notesField,
});
export type CodeKeyNodeUpdateBody = z.infer<typeof CodeKeyNodeUpdateBodySchema>;

/** Meerdere posities onder één ouder tegelijk (één regel per positie: cijfers + label). */
export const CodeKeyNodesBulkBodySchema = z.object({
  parent: digitsField(2, 19),
  lines: z
    .array(
      z.object({
        digits: digitsField(1, 2),
        label: labelField,
        kind: CodeKeyNodeKindSchema.default('class'),
      }),
    )
    .min(1)
    .max(200),
});
export type CodeKeyNodesBulkBody = z.infer<typeof CodeKeyNodesBulkBodySchema>;

export const CodeKeyBulkResultSchema = z.object({
  created: z.array(CodeKeyNodeRecordSchema),
  skipped: z.array(z.object({ digits: z.string(), reason: z.string() })),
});
export type CodeKeyBulkResult = z.infer<typeof CodeKeyBulkResultSchema>;

/** Kind in de boomverkenner: volgende positie + opgeslagen record (null bij geleend label of "alleen in Unit4"). */
export const CodeKeyBrowseChildSchema = CatalogSeriesNextSchema.extend({
  /** Aantal posities die onder deze positie beschreven zijn */
  node_count: z.number().int(),
  node: CodeKeyNodeRecordSchema.nullable(),
});
export type CodeKeyBrowseChild = z.infer<typeof CodeKeyBrowseChildSchema>;

export const CodeKeyBrowseSchema = z.object({
  prefix: z.string(),
  explanation: CodeExplanationSchema.nullable(),
  group: CodeKeyGroupRecordSchema.nullable(),
  /** Het record van de prefix zelf (null als die positie niet beschreven is) */
  node: CodeKeyNodeRecordSchema.nullable(),
  /** Aantal Unit4-codes die met de prefix beginnen */
  total: z.number().int(),
  children: z.array(CodeKeyBrowseChildSchema),
});
export type CodeKeyBrowse = z.infer<typeof CodeKeyBrowseSchema>;

export const CodeKeyInfoSchema = z.object({
  nodes: z.number().int(),
  groups: z.number().int(),
  source: z.enum(['database', 'seed']),
  loaded_at: z.string().nullable(),
});
export type CodeKeyInfo = z.infer<typeof CodeKeyInfoSchema>;

// ---------- Excel-export/-import (zelfde werkwijze als bij de sjablonen) ----------

export const CodeKeyImportActionSchema = z.enum(['create', 'update', 'unchanged', 'skip']);
export type CodeKeyImportAction = z.infer<typeof CodeKeyImportActionSchema>;

/** Eén regel uit het importrapport; ongewijzigde regels worden alleen geteld, niet opgesomd. */
export const CodeKeyImportRowSchema = z.object({
  sheet: z.enum(['groups', 'nodes']),
  row: z.number().int(),
  /** Hoofdgroep-cijfers of de code van de positie */
  code: z.string(),
  label: z.string(),
  action: CodeKeyImportActionSchema,
  /** Wat er verandert, of waarom de regel wordt overgeslagen */
  detail: z.string().nullable(),
});
export type CodeKeyImportRow = z.infer<typeof CodeKeyImportRowSchema>;

export const CodeKeyImportCountsSchema = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  skipped: z.number().int(),
});
export type CodeKeyImportCounts = z.infer<typeof CodeKeyImportCountsSchema>;

export const CodeKeyImportReportSchema = z.object({
  applied: z.boolean(),
  groups: CodeKeyImportCountsSchema,
  nodes: CodeKeyImportCountsSchema,
  /** Alleen create/update/skip; bij heel veel regels afgekapt (dan staat er een waarschuwing bij) */
  rows: z.array(CodeKeyImportRowSchema),
  warnings: z.array(z.string()),
});
export type CodeKeyImportReport = z.infer<typeof CodeKeyImportReportSchema>;
